import {
	type ColumnDef,
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
} from "@tanstack/react-table";
import Fuse from "fuse.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { TableVirtuoso } from "react-virtuoso";
import { ModalityIcon } from "./ModalityIcon";
import type { APIResponse, FlatModel, Provider } from "./types";

export function ModelsUI() {
	const providerParam = useParams().provider;
	const [data, setData] = useState<FlatModel[]>([]);
	const [providers, setProviders] = useState<Record<string, Provider>>({});
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState("");
	const [sorting, setSorting] = useState<SortingState>([]);

	const handleCopy = useCallback((text: string) => {
		navigator.clipboard.writeText(text);
	}, []);

	const searchRef = useCallback((node: HTMLInputElement | null) => {
		if (node) {
			const handleKeyDown = (e: KeyboardEvent) => {
				if ((e.metaKey || e.ctrlKey) && e.key === "k") {
					e.preventDefault();
					node.focus();
				}
			};
			window.addEventListener("keydown", handleKeyDown);
			return () => window.removeEventListener("keydown", handleKeyDown);
		}
	}, []);

	useEffect(() => {
		fetch("https://models.dev/api.json")
			.then((res) => res.json())
			.then((json: APIResponse) => {
				setProviders(json);
				const flat: FlatModel[] = [];
				Object.values(json).forEach((p) => {
					Object.values(p.models).forEach((m) => {
						flat.push({ ...m, providerId: p.id, providerName: p.name });
					});
				});
				setData(flat);
				setLoading(false);
			})
			.catch((err) => {
				console.error("Fetch error:", err);
				setLoading(false);
			});
	}, []);

	const activeProvider = useMemo(() => {
		if (!providerParam) {
			return null;
		}
		return (
			Object.values(providers).find(
				(p) => p.id.toLowerCase() === providerParam.toLowerCase(),
			) || null
		);
	}, [providers, providerParam]);

	const providerFilteredData = useMemo(() => {
		if (!providerParam) {
			return data;
		}
		return data.filter(
			(m) => m.providerId.toLowerCase() === providerParam.toLowerCase(),
		);
	}, [data, providerParam]);

	const fuse = useMemo(
		() =>
			new Fuse(providerFilteredData, {
				keys: ["name", "id", "providerName"],
				threshold: 0.3,
				distance: 100,
			}),
		[providerFilteredData],
	);

	const filteredData = useMemo(() => {
		if (!search) {
			return providerFilteredData;
		}
		return fuse.search(search).map((r) => r.item);
	}, [fuse, search, providerFilteredData]);

	const columns = useMemo<ColumnDef<FlatModel>[]>(
		() => [
			{
				accessorKey: "providerName",
				header: "Provider",
				size: 140,
				cell: ({ row }) => (
					<Link
						to={`/${row.original.providerId}`}
						style={{
							display: "flex",
							alignItems: "center",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							textDecoration: "none",
							color: "inherit",
						}}
					>
						<img
							src={`https://models.dev/logos/${row.original.providerId}.svg`}
							className="provider-logo"
							alt={row.original.providerName}
							onError={(e) =>
								((e.target as HTMLImageElement).src =
									"https://models.dev/logos/default.svg")
							}
							loading="lazy"
						/>
						{row.original.providerName}
					</Link>
				),
			},
			{
				accessorKey: "name",
				header: "Model",
				size: 180,
				cell: ({ getValue }) => (
					<div
						style={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						<strong>{getValue() as string}</strong>
					</div>
				),
			},
			{
				accessorKey: "id",
				header: "Model ID",
				size: 160,
				cell: ({ getValue }) => {
					const id = getValue() as string;
					return (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								overflow: "hidden",
							}}
						>
							{/** biome-ignore lint/a11y/useKeyWithClickEvents: we must make this copiable */}
							<code
								onClick={() => handleCopy(id)}
								title="Click to copy"
								style={{
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									flex: 1,
								}}
							>
								{id}
							</code>
							<button
								type="button"
								className="copy-btn"
								onClick={() => handleCopy(id)}
								title="Copy ID"
							>
								<svg
									width="14"
									height="14"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<title>Copy button</title>
									<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
									<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
								</svg>
							</button>
						</div>
					);
				},
			},
			{
				id: "limit",
				header: "Context",
				accessorFn: (row) => row.limit?.context ?? 0,
				size: 80,
				cell: ({ row }) => (
					<div style={{ textAlign: "right" }}>
						{(row.original.limit?.context ?? 0).toLocaleString()}
					</div>
				),
			},
			{
				id: "inputModality",
				header: "In",
				size: 50,
				cell: ({ row }) => (
					<div
						style={{ display: "flex", gap: "2px" }}
						title={`Input modalities: ${row.original.modalities?.input.join(", ")}`}
					>
						{row.original.modalities?.input.map((m) => (
							<ModalityIcon key={m} type={m} />
						))}
					</div>
				),
			},
			{
				id: "outputModality",
				header: "Out",
				size: 50,
				cell: ({ row }) => (
					<div
						style={{ display: "flex", gap: "2px" }}
						title={`Output modalities: ${row.original.modalities?.output.join(", ")}`}
					>
						{row.original.modalities?.output.map((m) => (
							<ModalityIcon key={m} type={m} />
						))}
					</div>
				),
			},
			{
				id: "cost",
				header: () => (
					<div style={{ textAlign: "right", width: "100%" }}>
						<div>Cost per 1M (In / Out)</div>
					</div>
				),
				accessorFn: (row) => row.cost?.input ?? 0,
				size: 140,
				cell: ({ row }) => {
					const cost = row.original.cost;
					if (!cost)
						return (
							<div style={{ textAlign: "right", color: "var(--text-muted)" }}>
								-
							</div>
						);
					return (
						<div
							style={{
								textAlign: "right",
								whiteSpace: "nowrap",
							}}
						>
							${cost.input.toFixed(2)} / ${cost.output.toFixed(2)}
						</div>
					);
				},
			},
			{
				accessorKey: "release_date",
				header: "Released",
				size: 100,
				cell: ({ getValue }) => (
					<div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
						{(getValue() as string) || "-"}
					</div>
				),
			},
			{
				id: "weights",
				header: "Weights",
				size: 90,
				cell: ({ row }) => (
					<div style={{ fontSize: "0.8rem" }}>
						{row.original.open_weights ? (
							<span style={{ color: "var(--accent-base)" }}>Open</span>
						) : (
							<span style={{ color: "var(--text-muted)" }}>Proprietary</span>
						)}
					</div>
				),
			},
			{
				id: "features",
				header: "Features",
				size: 160,
				cell: ({ row }) => (
					<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
						{row.original.reasoning && (
							<span className="badge badge-reasoning">Reasoning</span>
						)}
						{row.original.tool_call && (
							<span className="badge badge-tool">Tools</span>
						)}
						{row.original.attachment && (
							<span className="badge badge-vision">Vision</span>
						)}
					</div>
				),
			},
		],
		[handleCopy],
	);

	const table = useReactTable({
		data: filteredData,
		columns,
		state: { sorting },
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	});

	const { rows } = table.getRowModel();

	if (loading) return <div className="loading">Loading models...</div>;

	return (
		<div
			className="models-container"
			style={{ display: "flex", flexDirection: "column", height: "100%" }}
		>
			<div className="search-container">
				<Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
					<h1 style={{ margin: 0, fontSize: "1.2rem", whiteSpace: "nowrap" }}>
						mdl
					</h1>
				</Link>
				{providerParam && (
					<>
						<div
							style={{
								color: "var(--text-muted)",
								fontSize: "1.2rem",
								margin: "0 0.2rem",
							}}
						>
							/
						</div>
						<div
							style={{
								fontSize: "1.2rem",
								fontWeight: "bold",
								color: "var(--provider-title)",
								whiteSpace: "nowrap",
							}}
						>
							{activeProvider?.name || providerParam}
						</div>
						<div
							style={{ display: "flex", gap: "0.5rem", marginLeft: "0.5rem" }}
						>
							{activeProvider?.doc && (
								<a
									href={activeProvider.doc}
									target="_blank"
									rel="noreferrer"
									className="provider-link"
									title="Documentation"
								>
									Docs
								</a>
							)}
							{activeProvider?.npm && (
								<a
									href={`https://www.npmjs.com/package/${activeProvider.npm}`}
									target="_blank"
									rel="noreferrer"
									className="provider-link"
									title="NPM Package"
								>
									NPM
								</a>
							)}
						</div>
					</>
				)}
				<div
					style={{
						width: "1px",
						height: "24px",
						background: "var(--border-color)",
						margin: "0 0.5rem",
					}}
				/>
				<div
					style={{
						position: "relative",
						flex: 1,
						display: "flex",
						alignItems: "center",
					}}
				>
					<input
						type="text"
						className="search-input"
						ref={searchRef}
						placeholder={
							providerParam
								? `Search ${providerParam} models... (⌘K)`
								: "Quick search... (⌘K)"
						}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
					{search && (
						<button
							type="button"
							onClick={() => setSearch("")}
							style={{
								position: "absolute",
								right: "10px",
								background: "transparent",
								border: "none",
								color: "var(--text-muted)",
								cursor: "pointer",
								padding: "4px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
							title="Clear search"
						>
							<svg
								width="14"
								height="14"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<title>Clear</title>
								<line x1="18" y1="6" x2="6" y2="18"></line>
								<line x1="6" y1="6" x2="18" y2="18"></line>
							</svg>
						</button>
					)}
				</div>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "flex-end",
						minWidth: "100px",
					}}
				>
					<div
						style={{
							color: "var(--text-muted)",
							fontSize: "0.8rem",
							whiteSpace: "nowrap",
							textAlign: "right",
						}}
					>
						{filteredData.length} models
					</div>
					<div
						style={{
							color: "var(--text-muted)",
							fontSize: "0.6rem",
							whiteSpace: "nowrap",
							textAlign: "right",
							opacity: 0.7,
						}}
					>
						(by{" "}
						<a
							href="https://github.com/anomalyco/models.dev"
							target="_blank"
							rel="noreferrer"
							style={{ color: "inherit" }}
						>
							models.dev
						</a>
						)
					</div>
				</div>
			</div>

			<div className="table-wrapper">
				<TableVirtuoso
					totalCount={rows.length}
					fixedHeaderContent={() => {
						return table.getHeaderGroups().map((headerGroup) => (
							<tr
								key={headerGroup.id}
								style={{ background: "var(--header-bg)" }}
							>
								{headerGroup.headers.map((header) => (
									<th
										key={header.id}
										style={{
											width: header.getSize(),
											minWidth: header.getSize(),
											maxWidth: header.getSize(),
											background: "var(--header-bg)",
											overflow: "hidden",
										}}
										onClick={header.column.getToggleSortingHandler()}
									>
										<div
											style={{
												cursor: header.column.getCanSort()
													? "pointer"
													: "default",
												display: "flex",
												alignItems: "center",
												gap: "4px",
											}}
										>
											{flexRender(
												header.column.columnDef.header,
												header.getContext(),
											)}
											{header.column.getIsSorted() === "asc" && (
												<svg
													width="12"
													height="12"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<title>Ascending</title>
													<path d="m18 15-6-6-6 6" />
												</svg>
											)}
											{header.column.getIsSorted() === "desc" && (
												<svg
													width="12"
													height="12"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<title>Descending</title>
													<path d="m6 9 6 6 6-6" />
												</svg>
											)}
										</div>
									</th>
								))}
							</tr>
						));
					}}
					components={{
						Table: (props) => (
							<table
								{...props}
								style={{
									...props.style,
									borderCollapse: "collapse",
									tableLayout: "fixed",
									width: "100%",
								}}
							/>
						),
						TableRow: (props) => {
							const index = props["data-index"];
							const row = rows[index];
							if (!row) return null;
							return (
								<tr {...props}>
									{row.getVisibleCells().map((cell) => (
										<td
											key={cell.id}
											style={{
												width: cell.column.getSize(),
												minWidth: cell.column.getSize(),
												maxWidth: cell.column.getSize(),
												overflow: "hidden",
											}}
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</td>
									))}
								</tr>
							);
						},
					}}
					style={{ height: "100%" }}
				/>
			</div>
		</div>
	);
}
